import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link, RouteComponentProps } from '@reach/router';
import { DateTime } from 'luxon';
import { IconSize, Spinner2, Stack, Text2, HeaderText, VdsIcon } from '@el8/vital';
import {
  GetPracticeAvailabilityParams,
  useGetPracticeAvailability,
} from '@elation/api-client/src/booking';
import { ResponseStatus, ServiceLocationInfo } from 'types';
import { Box } from 'components';
import { getBookingInfo } from 'modules/booking';
import { useServiceLocations } from 'modules/serviceLocations';
import { formatPhone, notify } from 'utils';
import PhysicianAvailabilities from '../PhysicianAvailabilities/PhysicianAvailabilities';

import styles from './ServiceLocations.less';

type OwnProps = {
  practiceId: number;
  handleServiceLocationsMapPins: (filteredLocations: ServiceLocationInfo[]) => void;
} & RouteComponentProps;

const ServiceLocations = ({
  navigate,
  practiceId,
  handleServiceLocationsMapPins,
}: OwnProps): JSX.Element | null => {
  const [serviceLocations] = useServiceLocations(practiceId);
  const { selectedPhysicianId, selectedAppointmentTypeIds } = useSelector(getBookingInfo);
  const [filteredServiceLocations, setFilteredServiceLocations] = useState<
    ServiceLocationInfo[] | null
  >(null);

  const requestParams: GetPracticeAvailabilityParams = useMemo(() => {
    return {
      start_date: DateTime.fromObject({}).toISODate(),
    };
  }, []);

  const queryData = useGetPracticeAvailability(practiceId, requestParams, {
    query: {
      keepPreviousData: true,
    },
  });

  const { data: { data: availabilities, status } = {}, isFetched } = queryData;

  useEffect(() => {
    if (isFetched && status !== ResponseStatus.success) {
      notify('There was an error with your request. Please refresh the page.');
    }
  }, [isFetched, status]);

  useEffect(() => {
    if (status === ResponseStatus.success && serviceLocations?.length && availabilities) {
      const locationIdsWithHypotheticalAvailability = availabilities.map(
        (availability) => availability.service_location_id,
      );
      handleServiceLocationsMapPins(
        serviceLocations.filter((location) =>
          locationIdsWithHypotheticalAvailability.includes(location.id),
        ),
      );
    }
  }, [availabilities, handleServiceLocationsMapPins, serviceLocations, status]);

  useEffect(() => {
    if (status === ResponseStatus.success && serviceLocations && availabilities) {
      const filteredAvailabilityLocationIds = availabilities
        ?.filter(
          (availability) =>
            selectedAppointmentTypeIds.includes(availability.appointment_type_id) &&
            (selectedPhysicianId === null || selectedPhysicianId === availability.provider_id),
        )
        .map((availability) => availability.service_location_id);
      setFilteredServiceLocations(
        serviceLocations.filter((location) =>
          filteredAvailabilityLocationIds.includes(location.id),
        ),
      );
    }
  }, [selectedPhysicianId, serviceLocations, availabilities, selectedAppointmentTypeIds, status]);

  if (filteredServiceLocations?.length === 1) {
    const [location] = filteredServiceLocations;
    return (
      <>
        {!location.isPrimary ? (
          <div className={styles.locationHeader}>
            <HeaderText
              className={styles.locationName}
              legacyVariant="legacyTitleText"
              sizing="heading-lg"
              tag="h1"
            >
              {location.name}
            </HeaderText>
            <Text2 sizing="sm">{location.fullAddress}</Text2>
          </div>
        ) : null}
        <PhysicianAvailabilities
          navigate={navigate}
          practiceId={practiceId}
          serviceLocationId={filteredServiceLocations[0].id}
        />
      </>
    );
  }

  const renderSpinnerOrNoMatchingLocations = (): JSX.Element | undefined => {
    if (!filteredServiceLocations) {
      return (
        <Box className={styles.loadingOverlay} align="center" justify="center">
          <Spinner2 />
        </Box>
      );
    }

    if (filteredServiceLocations?.length) return undefined;

    return (
      <>
        <Text2 align="center" sizing="lg">
          <strong>No locations match your criteria</strong>
        </Text2>
        <Text2 align="center">
          Try changing your selection for Provider and/or Appointment Type(s)
        </Text2>
      </>
    );
  };

  return (
    <div className={styles.servicelocationsContainer}>
      <HeaderText
        className={styles.header}
        tag="h2"
        legacyVariant="legacyTitleText"
        sizing="heading-lg"
      >
        Locations with Available Appointments
      </HeaderText>
      {renderSpinnerOrNoMatchingLocations()}
      <Stack gap="xs">
        {filteredServiceLocations?.map((location) => (
          // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
          <article
            className={styles.serviceLocation}
            key={location.id}
            onClick={(): void => navigate(`service-locations/${location.id}`)}
          >
            <Box className={styles.locationInfo} direction="column">
              <HeaderText
                className={styles.locationName}
                tag="h3"
                legacyVariant="legacyTitleText"
                sizing="heading-lg"
              >
                {location.name}
              </HeaderText>
              <Text2 sizing="lg">
                {location.address}
                {location.suite && `, ${location.suite}`}, {location.city} {location.state}
              </Text2>
              <Text2 sizing="lg">{location.zip}</Text2>
              <Text2 sizing="lg">{formatPhone(location.phone)}</Text2>
            </Box>
            <Link
              className={styles.locationLink}
              onClick={(e: React.MouseEvent<HTMLAnchorElement, MouseEvent>): void =>
                e.stopPropagation()
              }
              to={`service-locations/${location.id}`}
            >
              <Text2 tag="strong" sizing="md">
                View Available Appointments
              </Text2>{' '}
              <VdsIcon icon="forward" size={IconSize.XSmall} />
            </Link>
          </article>
        ))}
      </Stack>
    </div>
  );
};

export default ServiceLocations;
